import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import "./../../styles/clients/news.css";
import NewsCard from "../../components/NewsCard";
import CategoryDropdown from "../../components/CategoryDropdown";
import Pagination from "../../components/Pagination";
import { Search } from "lucide-react";
import { PostsService, Post } from "../../services/postsService";
import { CategoriesService, Category } from "../../services/categoriesService";
import { formatDate } from "../../utils/dateFormatter";
import { toAbsoluteUrl } from "../../config/api";

const News: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [articles, setArticles] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [query, setQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tất cả");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Đọc currentPage từ URL params, mặc định là 1
  const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
  const [currentPage, setCurrentPage] = useState(pageFromUrl);
  const [itemsPerPage] = useState(24);
  const [total, setTotal] = useState(0); // Lưu tổng số bài viết từ API

  // Tính toán phân trang
  const totalPages = Math.ceil(articles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentArticles = articles.slice(startIndex, endIndex);

  // Tải danh mục
  const loadCategories = useCallback(async () => {
    if (categories.length === 0) {
      try {
        const categoriesData = await CategoriesService.getPublicCategories();
        setCategories(categoriesData);
      } catch (err) {}
    }
  }, [categories.length]);

  // Tải bài viết
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Tải bài viết
      const postsData = await PostsService.getPublicPosts({
        page: 1,
        limit: 1000, // Tăng limit để lấy tất cả bài viết
        search: query.trim() || undefined,
        category: selectedCategory !== "Tất cả" ? selectedCategory : undefined,
      });

      setArticles(postsData.posts);
      setTotal(postsData.total); // Lưu tổng số bài viết
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Có lỗi xảy ra khi tải dữ liệu"
      );
    } finally {
      setLoading(false);
    }
  }, [query, selectedCategory]);

  // Đồng bộ currentPage với URL params khi URL thay đổi
  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get("page") || "1", 10);
    setCurrentPage(pageFromUrl);
  }, [searchParams]);

  // Cập nhật URL khi currentPage thay đổi (từ pagination)
  const handlePageChange = useCallback((page: number) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (page === 1) {
      newSearchParams.delete("page");
    } else {
      newSearchParams.set("page", page.toString());
    }
    setSearchParams(newSearchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  // Reset về trang đầu khi tìm kiếm hoặc lọc thay đổi
  const prevQueryRef = useRef<string | null>(null);
  const prevCategoryRef = useRef<string | null>(null);
  
  useEffect(() => {
    // Chỉ reset nếu query hoặc category thực sự thay đổi (không phải lần đầu mount)
    const queryChanged = prevQueryRef.current !== null && prevQueryRef.current !== query;
    const categoryChanged = prevCategoryRef.current !== null && prevCategoryRef.current !== selectedCategory;
    
    if (queryChanged || categoryChanged) {
      // Reset về trang 1 và xóa page param khỏi URL
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete("page");
      setSearchParams(newSearchParams, { replace: true });
      setCurrentPage(1);
    }
    
    // Cập nhật refs
    prevQueryRef.current = query;
    prevCategoryRef.current = selectedCategory;
  }, [query, selectedCategory, searchParams, setSearchParams]);

  // Tải danh mục khi component được gắn kết
  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Tải dữ liệu khi query, category hoặc showAll thay đổi
  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCategory = (cat: string) => {
    setSelectedCategory(cat);
  };

  const handleSearch = () => {
    // Tìm kiếm sẽ được xử lý tự động qua useEffect
  };

  // Chuyển đổi dữ liệu bài viết cho NewsCard
  const transformPostToNewsItem = (post: Post) => {
    // Xử lý featuredImage: ưu tiên lấy từ database, chuyển đổi thành URL đầy đủ
    let imageUrl = "";
    if (post.featuredImage && post.featuredImage.trim()) {
      // Nếu là URL đầy đủ (http/https), giữ nguyên
      // Nếu là đường dẫn tương đối, chuyển đổi thành URL đầy đủ
      imageUrl = post.featuredImage.startsWith('http') 
        ? post.featuredImage 
        : toAbsoluteUrl(post.featuredImage);
    }
    
    return {
      id: post.id,
      slug: post.slug,
      title: post.title,
      date: formatDate(post.createdAt),
      author: post.author?.fullName || post.author?.email || "Tác giả",
      authorAvatar: toAbsoluteUrl(post.author?.avatar || ""),
      location: "Việt Nam",
      intro: post.metaDescription || post.content.substring(0, 150) + "...",
      content: post.content,
      imageUrl: imageUrl, // Chỉ truyền URL nếu có, NewsCard sẽ xử lý fallback
      insertedImageUrl: "/images/inserted-image-1.webp",
      tag: post.category?.name || "Khác",
      views: post.viewCount || 0,
      status: post.status,
      highlighted: [],
      related: [],
      externalLinks: post.externalLinks || [],
    };
  };

  return (
    <div className="news-page container">
      <div className="filter-bar">
        <CategoryDropdown
          onSelect={handleCategory}
          categories={categories}
          selectedCategory={selectedCategory}
        />
        <div className="search-box">
          <Search className="search-icon" size={18} />
          <input
            type="text"
            className="search-input"
            placeholder="Tìm kiếm theo tiêu đề, tên người dùng,..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          />
        </div>
        <button className="search-btn" onClick={handleSearch}>
          Tìm kiếm
        </button>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Đang tải dữ liệu...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p>Lỗi: {error}</p>
          <button onClick={loadData}>Thử lại</button>
        </div>
      )}

      {!loading && !error && (
        <>
          <div className="news-grid">
            {currentArticles.map((item) => (
              <div className="news-grid-item" key={item.id}>
                <NewsCard 
                  {...transformPostToNewsItem(item)} 
                  currentPage={currentPage}
                />
              </div>
            ))}
          </div>

          {articles.length === 0 && (
            <div className="no-results">
              <p>Không tìm thấy bài viết nào.</p>
            </div>
          )}

          {/* Phân trang */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </>
      )}
    </div>
  );
};

export default News;